import React, { useMemo } from "react";
import { Row, Col, Select, DatePicker, Button, Typography } from "antd";
import { useIntl } from "react-intl";
import { ClearOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
    ICalculateBrandEquityRequest,
    ISurveyCohort,
    IBrand,
    ISurvey,
    PeriodEnum,
} from "../../services/nestjs-base-api/types";
import { COUNTRY_OPTIONS, renderFlagIcon } from "../../constants/countries";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface BrandEquityFiltersProps {
    value: ICalculateBrandEquityRequest & {
        brandId?: number;
        periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        countryCodes?: string[];
    };
    onChange: (filters: ICalculateBrandEquityRequest & {
        brandId?: number;
        periodType?: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom';
        countryCodes?: string[];
    }) => void;
    cohorts: ISurveyCohort[];
    brands: IBrand[];
    surveys?: ISurvey[];
    onBrandChange: (brandId: number | undefined) => void;
}

const BrandEquityFilters: React.FunctionComponent<BrandEquityFiltersProps> = ({
    value,
    onChange,
    cohorts,
    brands,
    surveys,
    onBrandChange,
}) => {
    const intl = useIntl();
    const periodType = value.periodType || 'last30';

    // Get available countries from surveys for the selected brand
    const availableCountries = useMemo(() => {
        if (!value.brandId || !surveys) return [];

        // Handle both brandId and brand.id formats from different API responses
        const brandSurveys = surveys.filter(s => {
            const surveyBrandId = s.brandId || (s.brand as any)?.id;
            return surveyBrandId === value.brandId;
        });
        const countryCodes = Array.from(new Set(brandSurveys.map(s => s.countryCode).filter(Boolean)));

        return COUNTRY_OPTIONS.filter(country => countryCodes.includes(country.code));
    }, [value.brandId, surveys]);

    const handlePeriodTypeChange = (type: 'last30' | 'last60' | 'last90' | 'last120' | 'last180' | 'last365' | 'allTime' | 'custom') => {
        if (type !== 'custom') {
            // Map frontend period types to API PeriodEnum
            let period: PeriodEnum;
            switch (type) {
                case 'last30':
                    period = PeriodEnum.THIRTY_DAYS;
                    break;
                case 'last60':
                    period = PeriodEnum.SIXTY_DAYS;
                    break;
                case 'last90':
                    period = PeriodEnum.NINETY_DAYS;
                    break;
                case 'last120':
                    period = PeriodEnum.ONE_TWENTY_DAYS;
                    break;
                case 'last180':
                    period = PeriodEnum.ONE_EIGHTY_DAYS;
                    break;
                case 'last365':
                    period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                    break;
                case 'allTime':
                    period = PeriodEnum.ALL_TIME;
                    break;
                default:
                    period = PeriodEnum.THIRTY_DAYS;
            }

            // For predefined periods, set period field and clear dates
            onChange({
                ...value,
                periodType: type,
                period,
                dateFrom: undefined,
                dateTo: undefined,
            });
        } else {
            // For custom period, use DATE_RANGE period and clear dates initially
            onChange({
                ...value,
                periodType: type,
                period: PeriodEnum.DATE_RANGE,
                dateFrom: undefined,
                dateTo: undefined,
            });
        }
    };

    const handleCountryChange = (countryCodes: string[]) => {
        // If no countries selected, treat as "All Countries" (null)
        onChange({
            ...value,
            countryCodes: countryCodes.length > 0 ? countryCodes : undefined,
        });
    };

    const handleCohortChange = (cohortIds: number[]) => {
        // If no cohorts selected, treat as "All Cohorts" (null)
        onChange({
            ...value,
            cohortIds: cohortIds.length > 0 ? cohortIds : undefined,
        });
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates && dates[0] && dates[1]) {
            onChange({
                ...value,
                dateFrom: dates[0].toISOString(),
                dateTo: dates[1].toISOString(),
            });
        } else {
            onChange({
                ...value,
                dateFrom: undefined,
                dateTo: undefined,
            });
        }
    };

    const handleBrandChange = (brandId: number | undefined) => {
        // When brand changes, preserve period but reset cohorts and countries
        if (brandId && periodType !== 'custom') {
            // Map period type to PeriodEnum
            let period: PeriodEnum;
            switch (periodType) {
                case 'last30':
                    period = PeriodEnum.THIRTY_DAYS;
                    break;
                case 'last60':
                    period = PeriodEnum.SIXTY_DAYS;
                    break;
                case 'last90':
                    period = PeriodEnum.NINETY_DAYS;
                    break;
                case 'last120':
                    period = PeriodEnum.ONE_TWENTY_DAYS;
                    break;
                case 'last180':
                    period = PeriodEnum.ONE_EIGHTY_DAYS;
                    break;
                case 'last365':
                    period = PeriodEnum.THREE_SIXTY_FIVE_DAYS;
                    break;
                case 'allTime':
                    period = PeriodEnum.ALL_TIME;
                    break;
                default:
                    period = PeriodEnum.THIRTY_DAYS;
            }

            // Re-apply the period selection for the new brand
            onChange({
                ...value,
                brandId,
                periodType,
                period,
                dateFrom: undefined,
                dateTo: undefined,
                cohortIds: undefined, // Reset to "All Cohorts"
                countryCodes: undefined, // Reset to "All Countries"
            });
        } else if (brandId && periodType === 'custom') {
            // For custom period, preserve existing dates if any
            onChange({
                ...value,
                brandId,
                period: PeriodEnum.DATE_RANGE,
                cohortIds: undefined, // Reset to "All Cohorts"
                countryCodes: undefined, // Reset to "All Countries"
            });
        } else {
            onChange({
                ...value,
                brandId,
                period: undefined,
                cohortIds: undefined,
                countryCodes: undefined,
                dateFrom: undefined,
                dateTo: undefined,
            });
        }
        onBrandChange(brandId);
    };

    const handleClear = () => {
        onChange({
            brandId: undefined,
            cohortIds: undefined,
            countryCodes: undefined,
            period: PeriodEnum.THIRTY_DAYS,
            dateFrom: undefined,
            dateTo: undefined,
            periodType: 'last30',
        });
        onBrandChange(undefined);
    };

    const brandOptions = brands.map(brand => ({
        label: brand.name,
        value: brand.id,
    }));

    const cohortOptions = cohorts.map(cohort => {
        const displayName = cohort.displayName || cohort.name;
        const surveyCount = cohort.surveyCount || 0;
        return {
            label: `${displayName} (${surveyCount} survey${surveyCount !== 1 ? 's' : ''})`,
            value: cohort.id,
        };
    });

    const countryOptions = availableCountries.map(country => ({
        label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {renderFlagIcon(country.code, 16)}
                <span>{country.name}</span>
            </span>
        ),
        value: country.code,
    }));

    const hasFilters = value.brandId || value.cohortIds?.length || value.countryCodes?.length || (value.dateFrom && value.dateTo);

    return (
        <div>
            <Row gutter={[24, 16]} align="middle">
                {brands.length > 1 && (
                    <Col xs={24} md={8}>
                        <div style={{ marginBottom: '8px' }}>
                            <Text strong>
                                {intl.formatMessage({ id: 'brandEquity.filters.brand' })}
                            </Text>
                        </div>
                        <Select
                            placeholder={intl.formatMessage({ id: 'brandEquity.filters.brand.placeholder' })}
                            value={value.brandId}
                            onChange={handleBrandChange}
                            options={brandOptions}
                            style={{ width: '100%' }}
                            size="large"
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            allowClear
                        />
                    </Col>
                )}

                <Col xs={24} md={brands.length > 1 ? 8 : 12}>
                    <div style={{ marginBottom: '8px' }}>
                        <Text strong>
                            Countries
                        </Text>
                    </div>
                    <Select
                        mode="multiple"
                        placeholder="All Countries"
                        value={value.countryCodes}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        style={{ width: '100%' }}
                        size="large"
                        disabled={!value.brandId}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        maxTagCount="responsive"
                    />
                </Col>

                <Col xs={24} md={brands.length > 1 ? 8 : 12}>
                    <div style={{ marginBottom: '8px' }}>
                        <Text strong>
                            Customer Cohort
                        </Text>
                    </div>
                    <Select
                        mode="multiple"
                        placeholder="All Cohorts"
                        value={value.cohortIds}
                        onChange={handleCohortChange}
                        options={cohortOptions}
                        style={{ width: '100%' }}
                        size="large"
                        disabled={!value.brandId}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        maxTagCount="responsive"
                    />
                </Col>

                <Col xs={24} md={brands.length > 1 ? 8 : 12}>
                    <div style={{ marginBottom: '8px' }}>
                        <Text strong>
                            {intl.formatMessage({ id: 'brandEquity.filters.period' })}
                        </Text>
                    </div>
                    <Select
                        value={periodType}
                        onChange={handlePeriodTypeChange}
                        style={{ width: '100%' }}
                        size="large"
                        disabled={!value.brandId}
                        options={[
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last30' }), value: 'last30' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last60' }), value: 'last60' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last90' }), value: 'last90' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last120' }), value: 'last120' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last180' }), value: 'last180' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.last365' }), value: 'last365' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.allTime' }), value: 'allTime' },
                            { label: intl.formatMessage({ id: 'brandEquity.filters.period.custom' }), value: 'custom' },
                        ]}
                    />
                </Col>
            </Row>

            {periodType === 'custom' && (
                <Row gutter={[24, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24} md={{ span: brands.length > 1 ? 8 : 12, offset: brands.length > 1 ? 16 : 12 }}>
                        <div style={{ marginBottom: '8px' }}>
                            <Text strong>
                                {intl.formatMessage({ id: 'brandEquity.filters.dateRange' })}
                            </Text>
                        </div>
                        <RangePicker
                            placeholder={[
                                intl.formatMessage({ id: 'brandEquity.filters.dateFrom' }),
                                intl.formatMessage({ id: 'brandEquity.filters.dateTo' })
                            ]}
                            value={
                                value.dateFrom && value.dateTo
                                    ? [
                                        dayjs(value.dateFrom),
                                        dayjs(value.dateTo)
                                      ]
                                    : null
                            }
                            onChange={handleDateRangeChange}
                            style={{ width: '100%' }}
                            size="large"
                            disabled={!value.brandId}
                        />
                    </Col>
                </Row>
            )}

            {hasFilters && (
                <Row style={{ marginTop: '16px' }} justify="end">
                    <Col>
                        <Button
                            icon={<ClearOutlined />}
                            onClick={handleClear}
                            style={{ borderRadius: '6px' }}
                        >
                            {intl.formatMessage({ id: 'action.clear' })}
                        </Button>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default BrandEquityFilters;
